import { useState } from 'react';
import { X, Save, Database, Columns } from 'lucide-react';

const SaveDomainModal = ({ onClose, onSave, entityData, fieldData, modelType }) => {
  const isDomain = !!entityData; // Domain de tabela
  const isField = !!fieldData; // Domain de campo
  
  const [name, setName] = useState(
    isDomain ? `${entityData.name}_template` : fieldData ? `${fieldData.name}_template` : ''
  );
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      return;
    }

    setIsLoading(true);
    
    try {
      const domainData = {
        name: name.trim(),
        description: description.trim(),
        type: isDomain ? 'table' : 'field',
        modelType: modelType
      };

      if (isDomain) {
        // Save table domain
        domainData.nodeType = entityData.type;
        domainData.entityDescription = entityData.description || ''; // Entity description
        domainData.fields = entityData.fields || [];
        domainData.indexes = entityData.indexes || []; // Save indexes
        domainData.tags = entityData.tags || [];
        domainData.entityDomains = entityData.domains || []; // Save entity domain classifications
        domainData.color = entityData.color;
        domainData.sqlDefinition = entityData.sqlDefinition;
        domainData.materialized = entityData.materialized; // For materialized views
        domainData.businessRules = entityData.businessRules || null; // Business rules
      } else if (isField) {
        // Save field domain
        domainData.field = {
          name: fieldData.name,
          type: fieldData.type,
          nullable: fieldData.nullable,
          isPrimaryKey: fieldData.isPrimaryKey,
          unique: fieldData.unique,
          defaultValue: fieldData.defaultValue,
          description: fieldData.description || '',
          constraints: fieldData.constraints || []
        };
      }

      await onSave(domainData);
      onClose();
    } catch (error) {
      console.error('Error saving domain:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            {isDomain ? <Database size={20} /> : <Columns size={20} />}
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Save as Object
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-200"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              {isDomain ? (
                <>
                  💾 Saving <strong>"{entityData.name}"</strong> as a reusable table template.
                  <br />
                  Fields, attributes, and descriptions will be saved. Relationships will not be saved.
                </>
              ) : (
                <>
                  💾 Saving field <strong>"{fieldData?.name}"</strong> as a reusable field template.
                  <br />
                  Type, constraints, and attributes will be saved.
                </>
              )}
            </p>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Object Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="e.g., user_table_template, email_field_template"
              required
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
              placeholder="Describe the purpose of this object template..."
              rows="3"
            />
          </div>

          {/* Summary */}
          {isDomain && (
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <p className="font-medium mb-1">What will be saved:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>{entityData.fields?.length || 0} fields</li>
                <li>{entityData.tags?.length || 0} tags</li>
                {entityData.domains?.length > 0 && <li>{entityData.domains.length} domain(s)</li>}
                <li>Color and styling</li>
                {entityData.description && <li>Entity description</li>}
                {entityData.businessRules && <li>Business rules</li>}
                {entityData.sqlDefinition && <li>SQL definition (for views)</li>}
              </ul>
              <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                ⚠️ Relationships will NOT be saved
              </p>
            </div>
          )}

          {isField && (
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <p className="font-medium mb-1">Field properties:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Type: <strong>{fieldData.type}</strong></li>
                <li>Nullable: <strong>{fieldData.nullable ? 'Yes' : 'No'}</strong></li>
                {fieldData.isPrimaryKey && <li>Primary Key: <strong>Yes</strong></li>}
                {fieldData.unique && <li>Unique: <strong>Yes</strong></li>}
              </ul>
            </div>
          )}
        </form>

        {/* Actions */}
        <div className="flex gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || !name.trim()}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <Save size={16} />
            <span>{isLoading ? 'Saving...' : 'Save Object'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveDomainModal;
